import React from 'react';
import { useHistory, useLocation, Redirect } from 'react-router-dom';
import {
  Button,
  TextField,
  FormControlLabel,
  Checkbox,
  Typography,
  CssBaseline,
  Container,
  Snackbar,
  IconButton,
  InputAdornment,
  CircularProgress,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import { useAuthContext } from 'context/AuthContext';

import Visibility from '@material-ui/icons/Visibility';
import VisibilityOff from '@material-ui/icons/VisibilityOff';
import MuiAlert from '@material-ui/lab/Alert';
import TopBar from './TopBar';
import BottomBar from './BottomBar';
import { requestForToken } from 'init-firebase';

const Spinner = () => <CircularProgress style={{ color: 'white' }} />;

const useStyles = makeStyles((theme) => ({
  paper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  avatar: {
    marginBottom: theme.spacing(4),
    width: 420,
    borderRadius: 20,
    background:
      'transparent linear-gradient(0deg, #ffffff 0%, #ffffff 100%) 0% 0% no-repeat padding-box',
    height: 100,
  },
  form: {
    width: '100%', // Fix IE 11 issue.
    marginTop: theme.spacing(1),
  },
  submit: {
    margin: theme.spacing(3, 0, 2),
  },
  container: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
  },
}));

const Alert = (props) => <MuiAlert elevation={6} variant="filled" {...props} />;

export default function LoginScreen(props) {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const auth = useAuthContext();
  const [phone, setLogin] = React.useState('');
  const [otp, setPassword] = React.useState('');
  const [remember, setRemember] = React.useState(true);
  const [showPassword, setPasswordVisibility] = React.useState(false);
  const [mode, setMode] = React.useState('inputPhone');
  const [userMessage, setUserMessage] = React.useState({
    state: undefined,
    message: undefined,
  });
  const [loading, setLoading] = React.useState(false);

  const locationState = location.state || { from: '/' };
  if (!props.isFormWidget && auth.loggedIn) {
    return <Redirect to={locationState.from} />;
  }

  function handleLogin(e) {
    e.preventDefault();
    setLoading(true);
    auth
      .login({ phone, otp, remember })
      .then((response) => {
        setLoading(false);
        history.push(locationState.from);
        localStorage.setItem('client', true);
      })
      .catch((err) => {
        setLoading(false);
        setUserMessage({
          message: (err.error || {}).userMessage || 'Something went wrong.',
          state: 'error',
        });
      });
  }

  function handleChangeRememember({ target: { checked } }) {
    (props.setRemember || setRemember)(checked);
  }

  function handleClickShowPassword() {
    setPasswordVisibility(!showPassword);
  }
  const closeSnackbar = () =>
    (props.setUserMessage || setUserMessage)({
      state: undefined,
      message: undefined,
    });

  function requestOTP(e) {
    setLoading(true);
    e.preventDefault();
    if (/[0-9]{10}/.test(phone)) {
      auth
        .requestOTP(phone)
        .then((response) => {
          if (response.error) {
            setLoading(false);
            throw response;
          }
          (response.data || {}).userMessage &&
            setUserMessage({
              message: (response.data || {}).userMessage,
              state: 'message',
            });
          setLoading(false);
          setMode('inputOTP');
        })
        .catch((err) => {
          setLoading(false);
          setUserMessage({
            message: (err.error || {}).userMessage || 'Something went wrong.',
            state: 'error',
          });
        });
    } else {
      setLoading(false);
      alert('Please enter a valid phone number');
      return 0;
    }
  }

  const phoneField = {
    variant: 'outlined',
    margin: 'normal',
    inputProps: {
      maxlength: 10,
    },
    style: !props.isFormWidget
      ? {
          width: 525,
        }
      : {},
    required: true,
    fullWidth: true,
    id: 'phone',
    label: 'Mobile Number',
    placeholder: 'Enter your mobile number',
    onKeyPress: (e) => {
      const regex = /^[0-9]+$/;
      if (!regex.test(e.key)) {
        e.preventDefault();
      }
      if (e.key === 'Enter') {
        (props.requestOTP || requestOTP)(e);
      }
    },
    InputLabelProps: {
      shrink: true,
    },
    name: 'phone',
    value: props.phone || phone,
    onChange: (e) => (props.setLogin || setLogin)(e.target.value),
    autoComplete: 'phone',
    autoFocus: true,
  };

  const inputSets = {
    inputPhone: {
      fields: [phoneField],
      buttons: [
        {
          fullWidth: true,
          type: 'submit',
          variant: 'contained',
          color: 'primary',
          className: classes.submit,
          title: props.otpButtonText || 'GET OTP',
        },
      ],
      submitAction: props.requestOTP || requestOTP,
    },
    inputOTP: {
      fields: [
        {
          ...(phoneField || {}),
          disabled: true,
        },
        {
          variant: 'outlined',
          margin: 'normal',
          required: true,
          fullWidth: true,
          name: 'otp',
          value: props.otp || otp,
          onChange: (e) => (props.setPassword || setPassword)(e.target.value),
          label: 'OTP',
          type: showPassword ? 'text' : 'password',
          id: 'otp',
          autoComplete: 'current-otp',
          placeholder: 'Enter OTP',
          InputLabelProps: {
            shrink: true,
          },
          InputProps: {
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={handleClickShowPassword}
                >
                  {showPassword ? <Visibility /> : <VisibilityOff />}
                </IconButton>
              </InputAdornment>
            ),
          },
        },
      ],
      rememberMeEnabled: true,
      buttons: [
        {
          fullWidth: true,
          type: 'submit',
          variant: 'contained',
          color: 'primary',
          className: classes.submit,
          title: props.loginButtonText || 'LOGIN',
        },
      ],
      options: [
        {
          onClick: () => (props.setMode || setMode)('inputPhone'),
          color: 'primary',
          title: 'Try other phone number',
        },
        {
          onClick: props.requestOTP || requestOTP,
          color: 'primary',
          title: 'Request OTP again',
        },
      ],
      submitAction: props.handleLogin || handleLogin,
    },
  };

  return (
    <div className={classes.container}>
      {!props.isFormWidget && <TopBar />}
      <Container component="main" maxWidth="xs">
        <CssBaseline />
        <div
          style={
            !props.isFormWidget
              ? {
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '70%',
                }
              : {}
          }
        >
          <div className={classes.paper}>
            <Typography
              component="h1"
              variant="h5"
              style={{
                marginTop: props.isFormWidget && 16,
                marginBottom: 5,
                fontWeight: '600',
                fontSize: 28,
                maxWidth: 252,
                letterSpacing: 'normal',
              }}
            >
              {props.title || 'LOGIN USING OTP'}
            </Typography>
            <form
              className={classes.form}
              method="post"
              onSubmit={
                (inputSets[props.mode || mode] || {}).submitAction || (() => {})
              }
              noValidate
            >
              {((inputSets[props.mode || mode] || {}).fields || []).map(
                (field) => (
                  <TextField {...(field || {})} size="medium" />
                )
              )}
              {!props.isFormWidget &&
                (inputSets[props.mode || mode] || {}).rememberMeEnabled && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={remember}
                        onChange={handleChangeRememember}
                        color="primary"
                      />
                    }
                    label="Remember me"
                  />
                )}
              {((inputSets[props.mode || mode] || {}).buttons || []).length >
                0 && (
                <div
                  style={{ display: 'flex', flex: 1, alignItems: 'baseline' }}
                >
                  {((inputSets[props.mode || mode] || {}).buttons || []).map(
                    (button) => (
                      <Button
                        style={{
                          height: '60px',
                          width: !props.isFormWidget && '525px',
                          fontSize: '14px',
                          fontWeight: 'bold',
                          lineHeight: 'normal',
                          letterSpacing: 'normal',
                          borderRadius: '8px',
                        }}
                        {...button}
                      >
                        {loading ? <Spinner /> : button.title}
                      </Button>
                    )
                  )}
                </div>
              )}
              {((inputSets[props.mode || mode] || {}).options || []).length >
                0 && (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'baseline',
                    justifyContent: 'space-between',
                    marginTop: 20,
                  }}
                >
                  {((inputSets[props.mode || mode] || {}).options || []).map(
                    (option) => (
                      <Button {...option} style={{ padding: 0 }}>
                        {option.title}
                      </Button>
                    )
                  )}
                </div>
              )}
            </form>
          </div>
        </div>
        <Snackbar
          open={!!(props.userMessage || userMessage).message}
          onClose={closeSnackbar}
          autoHideDuration={6000}
          style={{ marginBottom: 90 }}
        >
          <Alert
            severity={
              (props.userMessage || userMessage)['state'] === 'error'
                ? 'error'
                : 'success'
            }
            onClose={closeSnackbar}
          >
            {(props.userMessage || userMessage).message}
          </Alert>
        </Snackbar>
      </Container>
      {!props.isFormWidget && <BottomBar />}
    </div>
  );
}
